import {defineCustomElement} from '../utils/defineCustomElement.js';
import {animateHeight} from '../modules/motion/height.js';
import {settle} from '../modules/motion/settle.js';

/**
 * Expand and scroll to an accordion item based on URL hash.
 * Handles hash links like #can-i-gift-an-ixdf-membership
 * @param {AccordionList} accordionList - The accordion list element to search within
 */
function expandAccordionFromHash(accordionList) {
    const hash = window.location.hash.slice(1);
    if (!hash) {
        return;
    }

    const targetElement = accordionList.querySelector(`#${CSS.escape(hash)}`);
    if (!targetElement) {
        return;
    }

    const details = targetElement.closest('details.js-accordionList__item');
    if (!details) {
        return;
    }

    details.open = true;

    requestAnimationFrame(() => {
        targetElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    });
}

/**
 * @component AccordionBehavior
 * @description Manages animated accordion functionality
 */
class AccordionBehavior {
    static config = {
        animation: {
            duration: 300,
            easing: 'ease-out',
        },
        selectors: {
            item: 'details.js-accordionList__item',
        },
        attributes: {
            initialized: 'data-accordion-initialized',
        },
    };

    /**
     * @param {HTMLDetailsElement} element
     */
    constructor(element) {
        this.details = element;
        this.summary = element.querySelector('summary');
        /** @type {Animation|null} */
        this.animation = null;
        this.isOpening = element.open;

        this.handleClick = this.onClick.bind(this);
        this.summary.addEventListener('click', this.handleClick);
        this.details.setAttribute(AccordionBehavior.config.attributes.initialized, 'true');
    }

    /**
     * @param {Event} event
     */
    onClick(event) {
        event.preventDefault();
        this.animateTo(this.animation ? !this.isOpening : !this.details.open);
    }

    /**
     * Read both heights at the click, each in its own state. A closed details skips the layout of its
     * content, so a height read while it is closed is 0 or stale.
     * @param {boolean} isOpening
     */
    animateTo(isOpening) {
        const startHeight = this.details.offsetHeight;
        this.animation?.cancel();
        this.details.open = isOpening;
        const endHeight = this.details.offsetHeight;
        this.details.open = true;

        this.isOpening = isOpening;
        const animation = animateHeight(this.details, startHeight, endHeight, AccordionBehavior.config.animation);
        this.animation = animation;
        settle(animation).then((playState) => {
            if (playState !== 'finished') {
                return;
            }
            this.details.open = isOpening;
            this.animation = null;
        });
    }

    destroy() {
        this.summary.removeEventListener('click', this.handleClick);
        this.animation?.cancel();
    }
}

class AccordionList extends HTMLElement {
    constructor() {
        super();
        this.accordions = new WeakMap();
    }

    connectedCallback() {
        this.addEventListener('click', this.handleClick.bind(this));

        // Expand accordion if URL hash matches an item in this list
        expandAccordionFromHash(this);

        // Handle hash changes for in-page navigation
        this.handleHashChange = () => expandAccordionFromHash(this);
        window.addEventListener('hashchange', this.handleHashChange);
    }

    /** Called each time the element is removed from the document. */
    disconnectedCallback() {
        window.removeEventListener('hashchange', this.handleHashChange);
        // WeakMap will handle cleanup of accordion instances
    }

    /**
     * @param {Event} event
     */
    handleClick(event) {
        const summary = event.target.closest('summary');
        if (!summary) return;

        const details = summary.closest(AccordionBehavior.config.selectors.item);
        if (!details || details.hasAttribute(AccordionBehavior.config.attributes.initialized)) return;

        const accordion = new AccordionBehavior(details);
        this.accordions.set(details, accordion);

        // Trigger the accordion's onClick method directly
        accordion.onClick(event);
    }
}

defineCustomElement('accordion-list', AccordionList);
